import React, { useMemo, useState } from 'react';
import { ImagePreview, Button, Modal } from 'tea-component';
import './index.scss';
// import Dialog from '@op/components/Aigc/components/dialog';
import { CompetitorsAdCreativesItem } from '@opm/services';
import { DBO_U } from '@op/utils/format';
import useIntls from '@opm/hooks/useIntls';
import { NO_DATE } from '@op/constants';
import { ClassSpecial } from '@op/constants/common';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { TOpenWindowsType } from '../type';

export interface MediaPreviewModalProps {
  /** 是否显示弹窗 */
  visible: boolean;
  /** 关闭弹窗回调 */
  onClose: () => void;
  /** 预览图片 URL (preview_img) */
  imageUrl?: string;
  /** 视频 URL (video_url) */
  videoUrl?: string;
  /** 标题 */
  title?: string;
  /** 原始链接 (origin_url) - 用于外部跳转 */
  originUrl?: string;
}

/**
 * 媒体预览弹窗组件
 * 支持图片预览和视频播放
 */
export default function MediaPreviewModal({
  visible,
  onClose,
  imageUrl,
  videoUrl,
  title,
  originUrl,
}: MediaPreviewModalProps) {
  const hasVideo = !!videoUrl;
  const hasImage = !!imageUrl;
  const { intl } = useIntls();
  // 处理外部链接跳转
  const handleOpenExternal = () => {
    if (originUrl) {
      DBO_U.open(originUrl);
    }
  };

  return (
    <Modal
      className="dbom-media-preview-modal"
      visible={visible}
      caption={title}
      onClose={onClose}
      size="l"
    >
      <div className="dbom-media-preview-content">
        {/* 视频 */}
        {hasVideo && (
          <div className="dbom-media-preview-video">
            <video
              src={videoUrl}
              title={title}
              className="dbom-media-preview-video-player"
              controls
              autoPlay
              loop
            />
          </div>
        )}

        {/* 图片预览 */}
        {!hasVideo && hasImage && (
          <div className="dbom-media-preview-image">
            <ImagePreview
              src={imageUrl}
              previewTitle={title}
              previewSrc={imageUrl}
              className={ClassSpecial.FILTER_NONE}
              modalClassName='dbom-media-preview-image__modal'
            ></ImagePreview>
          </div>
        )}

        {/* 无内容提示 */}
        {!hasVideo && !hasImage && (
          <div className="dbom-media-preview-empty">{intl(NO_DATE.SIMPLE)}</div>
        )}

        <Modal.Footer>
          {originUrl && (
            <Button
              type="primary"
              onClick={handleOpenExternal}
              className="dbom-media-preview-external-btn"
            >
              {intl('Go to original URL')}
            </Button>
          )}
          <Button type="weak" onClick={onClose}>
            {intl('Close')}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}

/**
 * Hook: 简化版媒体预览弹窗管理
 * 直接传入 CompetitorsAdCreativesItem 数据，自动解析字段
 */
export function useMediaPreviewModal() {
  const [visible, setVisible] = useState(false);
  const [mediaData, setMediaData] = useState<CompetitorsAdCreativesItem>();

  function copeResources(mediaData: CompetitorsAdCreativesItem) {
    const { resources } = mediaData;
    try {
      const data = JSON.parse(resources);
      return data?.[0];
    } catch (error) { }
  }

  const open = (
    data: CompetitorsAdCreativesItem,
    type: TOpenWindowsType = 'model',
  ) => {
    const { type: dataType, preview_img, origin_url, game_id } = data;

    // 埋点：区分封面点击和标题点击
    DBOM_POINT.btn(type === 'ori' ? 'AdTitle' : 'AdCover');

    if (dataType.includes('playable')) {
      DBO_U.open(origin_url || preview_img);
      return;
    }
    if (type === 'game') {
      if (!game_id) return;
      DBO_U.go({
        path: `/opinion/AdDeliveryOverview?gameid=${game_id}`,
        params: {},
      });
      return;
    }

    if (type === 'model') {
      setMediaData(data);
      setVisible(true);
      return;
    }

    if (dataType.includes('image')) {
      DBO_U.open(copeResources(data));
      return;
    }

    DBO_U.open(origin_url);
  };

  const close = () => setVisible(false);

  // 从 CompetitorsAdCreativesItem 解析出弹窗所需数据
  const previewData = useMemo(() => {
    if (!mediaData) return null;
    const { type, app_name, origin_url, preview_img } = mediaData;
    let videoUrl: any = '';
    if (type.includes('video')) {
      try {
        videoUrl = copeResources(mediaData);
      } catch (error) { }
    }

    return {
      videoUrl,
      imageUrl: preview_img,
      title: app_name,
      originUrl: origin_url,
    };
  }, [mediaData]);

  return {
    visible,
    open,
    close,
    previewData,
  };
}
